import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNotification } from '@ipc/ipc-components';
import { Cn54bisCn56SummaryData, getDisabledFieldsCn54bis } from './Cn54bisCn56SummaryTable';
import { useCn54bisCn56BilateralMutation, Cn54bisCn56BilateralRequest } from '../hooks/useCn54bisCn56BilateralMutation';

interface EditCn54bisCn56ModalProps {
  open: boolean;
  onClose: () => void;
  data: Cn54bisCn56SummaryData | null;
  onSave: (updatedData: Cn54bisCn56SummaryData) => void;
  onShowSnackbar?: (message: string, severity: 'error' | 'warning' | 'info' | 'success') => void;
}

interface FormData {
  // Basic info (read-only)
  sendingPpo: string;
  receivingPpo: string;
  year: string;
  service: string;
  mailCategory: string;
  format: string;
  product: string;
  
  // Editable bilateral fields - using the correct field names from the interface
  bilateralItems: string;
  bilateralGrossWeight: string;
  bilateralGrossIpk: string;
  bilateralGross: string;
  // Additional Gross % fields for connected rows (4, 5, 6)
  bilateralGrossP: string;
  bilateralGrossG: string;
  bilateralGrossE: string;
}

interface FormErrors {
  bilateralItems?: string;
  bilateralGrossWeight?: string;
  bilateralGrossIpk?: string;
  bilateralGross?: string;
  bilateralGrossP?: string;
  bilateralGrossG?: string;
  bilateralGrossE?: string;
}

export function EditCn54bisCn56Modal({ open, onClose, data, onSave, onShowSnackbar }: EditCn54bisCn56ModalProps) {
  const { successNotification, errorNotification } = useNotification();
  
  const [formData, setFormData] = useState<FormData>({
    sendingPpo: '',
    receivingPpo: '',
    year: '',
    service: '',
    mailCategory: '',
    format: '',
    product: '',
    bilateralItems: '',
    bilateralGrossWeight: '',
    bilateralGrossIpk: '',
    bilateralGross: '',
    bilateralGrossP: '',
    bilateralGrossG: '',
    bilateralGrossE: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const bilateralMutation = useCn54bisCn56BilateralMutation({
    onSuccess: () => {
      console.log('Mutation onSuccess called'); // Debug log
      
      // Try to show success notification, but don't let it break the flow
      try {
                 successNotification('CN54bis CN56 bilateral data updated successfully');
      } catch (error) {
        console.warn('Failed to show success notification:', error);
        // Fallback to snackbar if notification fails
        if (onShowSnackbar) {
                     onShowSnackbar('CN54bis CN56 bilateral data updated successfully', 'success');
        }
      }
      
      // Let the parent component handle closing the modal and refreshing data
      if (data && onSave) {
        console.log('Calling onSave with data:', data); // Debug log
        const updatedData: Cn54bisCn56SummaryData = {
          ...data,
          bilateralItems: formData.bilateralItems ? parseFloat(formData.bilateralItems) : null,
          bilateralGrossWeight: formData.bilateralGrossWeight ? parseFloat(formData.bilateralGrossWeight) : null,
          bilateralGrossIpk: formData.bilateralGrossIpk ? parseFloat(formData.bilateralGrossIpk) : null,
          bilateralGross: formData.bilateralGross ? parseFloat(formData.bilateralGross) : null,
          rowIndex: data.rowIndex, // Preserve the row index
        };
        console.log('Calling onSave with updatedData:', updatedData); // Debug log
        
        try {
          onSave(updatedData);
          console.log('onSave called successfully'); // Debug log
        } catch (error) {
          console.error('Error calling onSave:', error);
          // Fallback notification
          if (onShowSnackbar) {
            onShowSnackbar('Record updated but failed to refresh data', 'warning');
          }
        }
      } else {
        console.log('onSave not called - data or onSave missing:', { data: !!data, onSave: !!onSave }); // Debug log
      }
    },
    onError: (error) => {
      console.error('API Error:', error);
      
      let errorMessage = 'Failed to update bilateral data';
      let errorMessages: string[] = [];
      
      // Handle different error response structures
      if (error?.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
          errorMessages = [errorData];
        } else if (Array.isArray(errorData)) {
          // Handle array of error messages directly
          errorMessages = errorData;
          errorMessage = errorData.join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
          errorMessages = Array.isArray(errorData.message) ? errorData.message : [errorData.message];
        } else if (errorData.error) {
          errorMessage = errorData.error;
          errorMessages = Array.isArray(errorData.error) ? errorData.error : [errorData.error];
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessages = errorData.errors;
          errorMessage = errorData.errors.join(', ');
        }
      } else if (error?.message) {
        errorMessage = error.message;
        errorMessages = [error.message];
      }
      
      // Show error notification
      try {
        errorNotification(errorMessage);
      } catch (notificationError) {
        console.warn('Failed to show error notification:', notificationError);
      }
      
      // Also show snackbar if provided
      if (onShowSnackbar) {
        // Show combined error message in snackbar
        // If we have multiple error messages, join them with newlines or commas
        const snackbarMessage = errorMessages.length > 0 
          ? errorMessages.join(', ') 
          : errorMessage;
        onShowSnackbar(snackbarMessage, 'error');
      }
    }
  });

  // Helper function to format number with thousand separators (apostrophes)
  const formatWithThousandSeparator = (value: string, allowDecimals: boolean = false): string => {
    if (!value || value.trim() === '') return '';
    
    // Remove existing apostrophes for processing
    let cleaned = value.replace(/'/g, '');
    
    // Check if negative
    const isNegative = cleaned.startsWith('-');
    if (isNegative) {
      cleaned = cleaned.substring(1);
    }
    
    // Split by decimal point if decimals are allowed
    let integerPart = cleaned;
    let decimalPart = '';
    if (allowDecimals && cleaned.includes('.')) {
      const parts = cleaned.split('.');
      integerPart = parts[0];
      decimalPart = parts.slice(1).join('');
    }
    
    // Add thousand separators to integer part (from right to left, every 3 digits)
    if (integerPart) {
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return (isNegative ? '-' : '') + formattedInteger + (decimalPart ? '.' + decimalPart : '');
    }
    
    return (isNegative ? '-' : '') + cleaned;
  };

  // Update form data when data prop changes
  useEffect(() => {
    if (data) {
      const rowIndex = data.rowIndex || 0;
      const isConnectedRow = rowIndex >= 3 && rowIndex <= 5; // Rows 4, 5, 6 (0-based: 3, 4, 5)
      
      // For connected rows (4, 5, 6), try to get data from connected rows
      let grossPValue = '';
      let grossGValue = '';
      let grossEValue = '';
      
      if (isConnectedRow && data.connectedRows) {
        // Find values from connected rows
        const row4 = data.connectedRows.find(r => r.rowIndex === 3); // Row 4
        const row5 = data.connectedRows.find(r => r.rowIndex === 4); // Row 5  
        const row6 = data.connectedRows.find(r => r.rowIndex === 5); // Row 6
        
        // Load bilateral values from connected rows, or use current row if it's the right row
        grossPValue = row4?.bilateralGross?.toString() || (rowIndex === 3 ? data.bilateralGross?.toString() : '') || '';
        grossGValue = row5?.bilateralGross?.toString() || (rowIndex === 4 ? data.bilateralGross?.toString() : '') || '';
        grossEValue = row6?.bilateralGross?.toString() || (rowIndex === 5 ? data.bilateralGross?.toString() : '') || '';
      } else if (isConnectedRow) {
        // If no connected rows data, use current row data based on row index
        if (rowIndex === 3) grossPValue = data.bilateralGross?.toString() || '';
        if (rowIndex === 4) grossGValue = data.bilateralGross?.toString() || '';
        if (rowIndex === 5) grossEValue = data.bilateralGross?.toString() || '';
      }
      
      // Helper function to format value with thousand separators based on field type
      const formatValue = (val: string | number | null | undefined, fieldType: 'integer' | 'decimal' | 'decimal4' | 'percent'): string => {
        if (val === null || val === undefined || val === '') return '';
        const strVal = val.toString();
        if (strVal.trim() === '') return '';
        
        switch (fieldType) {
          case 'integer':
            return formatWithThousandSeparator(strVal);
          case 'decimal':
            return formatWithThousandSeparator(strVal, true);
          case 'decimal4':
            return formatWithThousandSeparator(strVal, true);
          case 'percent':
            return formatWithThousandSeparator(strVal, true);
          default:
            return strVal;
        }
      };
      
      setFormData({
        sendingPpo: data.sendingPpo || '',
        receivingPpo: data.receivingPpo || '',
        year: data.year || '',
        service: data.service || '',
        mailCategory: data.mailCategory || '',
        format: data.format || '',
        product: data.product || '',
        bilateralItems: formatValue(data.bilateralItems, 'integer'),
        bilateralGrossWeight: formatValue(data.bilateralGrossWeight, 'decimal'),
        bilateralGrossIpk: formatValue(data.bilateralGrossIpk, 'decimal4'),
        bilateralGross: formatValue(data.bilateralGross, 'percent'),
        bilateralGrossP: formatValue(grossPValue, 'percent'),
        bilateralGrossG: formatValue(grossGValue, 'percent'),
        bilateralGrossE: formatValue(grossEValue, 'percent'),
      });
    }
  }, [data]);

  // Validation and formatting functions
  const validateInteger = (value: string, fieldName: string): { value: string; error?: string } => {
    // Remove apostrophes temporarily for validation
    const withoutSeparators = value.replace(/'/g, '');
    
    // Check if the original value contained a decimal point
    if (withoutSeparators.includes('.')) {
      const cleaned = withoutSeparators.replace(/[^\d-]/g, '');
      return { value: formatWithThousandSeparator(cleaned), error: `${fieldName} must be an integer (no decimal places allowed)` };
    }
    
    // Allow digits, minus sign, and apostrophes
    // Remove invalid characters but keep apostrophes for now
    let cleaned = value.replace(/[^\d'-]/g, '');
    
    // Ensure only one minus sign at the beginning
    const hasMinus = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');
    if (hasMinus) {
      cleaned = '-' + cleaned;
    }
    
    // Get value without separators for validation
    const withoutSeparatorsForValidation = cleaned.replace(/'/g, '');
    
    // Check for valid integer format (after removing separators)
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && !/^-?\d+$/.test(withoutSeparatorsForValidation)) {
      return { value: formatWithThousandSeparator(withoutSeparatorsForValidation), error: 'Invalid integer format' };
    }
    
    // Format with thousand separators if valid
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && /^-?\d+$/.test(withoutSeparatorsForValidation)) {
      cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation);
    }
    
    return { value: cleaned };
  };

  const validateDecimal = (value: string, requiredDecimals: number, fieldName: string): { value: string; error?: string } => {
    // Remove apostrophes temporarily for validation
    const withoutSeparators = value.replace(/'/g, '');
    
    // Allow digits, decimal point, minus sign, and apostrophes
    // Remove invalid characters but keep apostrophes for now
    let cleaned = value.replace(/[^\d.'-]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Ensure only one minus sign at the beginning
    const hasMinus = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');
    if (hasMinus) {
      cleaned = '-' + cleaned;
    }
    
    // Get value without separators for validation
    const withoutSeparatorsForValidation = cleaned.replace(/'/g, '');
    
    // Check for valid decimal format (after removing separators)
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && !/^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
      return { value: formatWithThousandSeparator(withoutSeparatorsForValidation, true), error: 'Invalid decimal format' };
    }
    
    // Check if user is still typing (incomplete decimal)
    const decimalParts = withoutSeparatorsForValidation.split('.');
    const isTypingDecimal = decimalParts.length === 2 && decimalParts[1].length < requiredDecimals;
    const endsWithDecimalPoint = withoutSeparatorsForValidation.endsWith('.');
    
    // If value is not empty and not incomplete, check decimal places requirement
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && !isTypingDecimal && !endsWithDecimalPoint) {
      if (decimalParts.length === 2) {
        const decimalPlaces = decimalParts[1].length;
        if (decimalPlaces !== requiredDecimals) {
          return { 
            value: formatWithThousandSeparator(withoutSeparatorsForValidation, true), 
            error: `${fieldName} must have exactly ${requiredDecimals} decimal place${requiredDecimals > 1 ? 's' : ''}` 
          };
        }
      } else if (decimalParts.length === 1 && requiredDecimals > 0 && !withoutSeparatorsForValidation.endsWith('.')) {
        // Value has no decimal point but decimals are required (only show error if not typing)
        return { 
          value: formatWithThousandSeparator(withoutSeparatorsForValidation, true), 
          error: `${fieldName} must have exactly ${requiredDecimals} decimal place${requiredDecimals > 1 ? 's' : ''}` 
        };
      }
      
      // Format with thousand separators only if value is complete (has required decimals or is integer without decimals)
      if (/^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
        // Only format if decimal part is complete or doesn't exist
        if (decimalParts.length === 1 || (decimalParts.length === 2 && decimalParts[1].length === requiredDecimals)) {
          cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation, true);
        } else if (decimalParts.length === 2 && decimalParts[1].length > 0) {
          // Format integer part only, keep decimal part as-is while typing
          const formattedInteger = formatWithThousandSeparator(decimalParts[0] || '0', false);
          cleaned = formattedInteger + '.' + decimalParts[1];
          if (hasMinus && !cleaned.startsWith('-')) {
            cleaned = '-' + cleaned;
          }
        }
      }
    } else if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && /^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
      // While typing, format only the integer part before the decimal point
      if (decimalParts.length === 2) {
        const formattedInteger = formatWithThousandSeparator(decimalParts[0] || '0', false);
        cleaned = formattedInteger + '.' + decimalParts[1];
        if (hasMinus && !cleaned.startsWith('-')) {
          cleaned = '-' + cleaned;
        }
      } else if (decimalParts.length === 1 && !endsWithDecimalPoint) {
        // Format integer part if no decimal point
        cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation, false);
      }
    }
    
    return { value: cleaned };
  };

  const validateDecimalWithPercent = (value: string, requiredDecimals: number, fieldName: string): { value: string; error?: string } => {
    // Remove % sign for validation, but allow it in the display value
    const hasPercent = value.trim().endsWith('%');
    let valueWithoutPercent = hasPercent ? value.trim().slice(0, -1).trim() : value.trim();
    
    // Remove apostrophes temporarily for validation
    const withoutSeparators = valueWithoutPercent.replace(/'/g, '');
    
    // Allow digits, decimal point, minus sign, and apostrophes
    // Remove invalid characters but keep apostrophes for now
    let cleaned = valueWithoutPercent.replace(/[^\d.'-]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Ensure only one minus sign at the beginning
    const hasMinus = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');
    if (hasMinus) {
      cleaned = '-' + cleaned;
    }
    
    // Get value without separators for validation
    const withoutSeparatorsForValidation = cleaned.replace(/'/g, '');
    
    // Check for valid decimal format (after removing separators)
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && !/^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
      // This shouldn't happen after cleaning, but keep as safety check
      return { value: formatWithThousandSeparator(withoutSeparatorsForValidation, true) + (hasPercent ? '%' : '') };
    }
    
    // Check if user is still typing (incomplete decimal)
    const decimalParts = withoutSeparatorsForValidation.split('.');
    const isTypingDecimal = decimalParts.length === 2 && decimalParts[1].length < requiredDecimals;
    const endsWithDecimalPoint = withoutSeparatorsForValidation.endsWith('.');
    
    // If value is not empty and not incomplete, check decimal places requirement
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && !isTypingDecimal && !endsWithDecimalPoint) {
      if (decimalParts.length === 2) {
        const decimalPlaces = decimalParts[1].length;
        if (decimalPlaces !== requiredDecimals) {
          return { 
            value: formatWithThousandSeparator(withoutSeparatorsForValidation, true) + (hasPercent ? '%' : ''), 
            error: `${fieldName} must have exactly ${requiredDecimals} decimal place${requiredDecimals > 1 ? 's' : ''}` 
          };
        }
      } else if (decimalParts.length === 1 && requiredDecimals > 0 && !withoutSeparatorsForValidation.endsWith('.')) {
        // Value has no decimal point but decimals are required (only show error if not typing)
        return { 
          value: formatWithThousandSeparator(withoutSeparatorsForValidation, true) + (hasPercent ? '%' : ''), 
          error: `${fieldName} must have exactly ${requiredDecimals} decimal place${requiredDecimals > 1 ? 's' : ''}` 
        };
      }
      
      // Format with thousand separators only if value is complete (has required decimals or is integer without decimals)
      if (/^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
        // Only format if decimal part is complete or doesn't exist
        if (decimalParts.length === 1 || (decimalParts.length === 2 && decimalParts[1].length === requiredDecimals)) {
          cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation, true);
        } else if (decimalParts.length === 2 && decimalParts[1].length > 0) {
          // Format integer part only, keep decimal part as-is while typing
          const formattedInteger = formatWithThousandSeparator(decimalParts[0] || '0', false);
          cleaned = formattedInteger + '.' + decimalParts[1];
          if (hasMinus && !cleaned.startsWith('-')) {
            cleaned = '-' + cleaned;
          }
        }
      }
    } else if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && /^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
      // While typing, format only the integer part before the decimal point
      if (decimalParts.length === 2) {
        const formattedInteger = formatWithThousandSeparator(decimalParts[0] || '0', false);
        cleaned = formattedInteger + '.' + decimalParts[1];
        if (hasMinus && !cleaned.startsWith('-')) {
          cleaned = '-' + cleaned;
        }
      } else if (decimalParts.length === 1 && !endsWithDecimalPoint) {
        // Format integer part if no decimal point
        cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation, false);
      }
    }
    
    // Return value with % sign if it was present
    return { value: cleaned + (hasPercent ? '%' : '') };
  };

  const formatForDisplay = (value: string, fieldType: 'integer' | 'decimal'): string => {
    if (!value || value.trim() === '') return '';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    
    switch (fieldType) {
      case 'integer':
        return Math.round(numValue).toString();
      case 'decimal':
        return numValue.toString();
      default:
        return value;
    }
  };

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = event.target.value;
    let error: string | undefined;
    
    // Apply validation based on field type
    switch (field) {
      case 'bilateralItems':
        const intResult = validateInteger(value, 'CN56 Items');
        value = intResult.value;
        error = intResult.error;
        break;
      case 'bilateralGrossWeight':
        const weightResult = validateDecimal(value, 1, 'CN56 Gross Weight'); // 1 decimal place
        value = weightResult.value;
        error = weightResult.error;
        break;
      case 'bilateralGrossIpk':
        const ipkResult = validateDecimal(value, 4, 'CN54bis Gross IPK'); // 4 decimal places
        value = ipkResult.value;
        error = ipkResult.error;
        break;
      case 'bilateralGross':
      case 'bilateralGrossP':
      case 'bilateralGrossG':
      case 'bilateralGrossE':
        const grossResult = validateDecimalWithPercent(value, 2, 'CN54bis Gross%'); // 2 decimal places, allow %
        value = grossResult.value;
        error = grossResult.error;
        break;
      default:
        // No validation for other fields
        break;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Update errors
    setFormErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  // Helper function to clean and convert values to strings (as per Swagger schema)
  const cleanStringValue = (value: string | undefined | null): string => {
    if (!value || value.trim() === '') return '';
    
    // Remove thousand separators (apostrophes) and trim whitespace
    let cleaned = value.replace(/'/g, '').trim();
    
    // Remove % sign if present (for Gross% fields)
    cleaned = cleaned.replace(/%/g, '').trim();
    
    // Handle negative numbers properly
    if (cleaned.startsWith('-')) {
      cleaned = '-' + cleaned.substring(1).replace(/^-/g, '');
    }
    
    // Ensure proper decimal formatting
    if (cleaned.includes('.')) {
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
    }
    
    return cleaned;
  };

  const handleSave = () => {
    if (!data) return;

    // Check for validation errors
    const hasErrors = Object.values(formErrors).some(error => !!error);
    if (hasErrors) {
      errorNotification('Please fix validation errors before saving');
      return;
    }

    // Basic validation
    if (!data.sendingPpo || !data.receivingPpo) {
      errorNotification('Sending PPO and Receiving PPO are required');
      return;
    }

    console.log('handleSave called with data:', data); // Debug log

    // Helper function to determine row type from row index
    const getRowType = (rowIndex: number): string => {
      const rowTypes = [
        'ROW_1', 'ROW_2', 'ROW_3', 'ROW_4', 'ROW_5', 'ROW_6', 'ROW_7', 'ROW_8', 'ROW_9', 'ROW_10',
        'ROW_11', 'ROW_12', 'ROW_13', 'ROW_14', 'ROW_15', 'ROW_16', 'ROW_17'
      ];
      return rowTypes[rowIndex] || 'ROW_1';
    };

    // Helper function to calculate unsigned value and delta sign
    const calculateUnsignedAndSign = (value: string): { unsigned: number; deltaSign: string } => {
      if (!value || value.trim() === '') {
        return { unsigned: 0, deltaSign: '' };
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return { unsigned: 0, deltaSign: '' };
      }
      return {
        unsigned: Math.abs(numValue),
        deltaSign: numValue >= 0 ? '' : '-'
      };
    };

    // Calculate unsigned values and delta signs for bilateral fields
    const itemsCalc = calculateUnsignedAndSign(formData.bilateralItems);
    const weightCalc = calculateUnsignedAndSign(formData.bilateralGrossWeight);
    const ipkCalc = calculateUnsignedAndSign(formData.bilateralGrossIpk);
    const grossCalc = calculateUnsignedAndSign(formData.bilateralGross);
    const grossPCalc = calculateUnsignedAndSign(formData.bilateralGrossP);
    const grossGCalc = calculateUnsignedAndSign(formData.bilateralGrossG);
    const grossECalc = calculateUnsignedAndSign(formData.bilateralGrossE);

    // Helper function to clean value by removing thousand separators and % sign
    const cleanValueForApi = (value: string | null | undefined): string | null => {
      if (!value || value.trim() === '') return null;
      // Remove thousand separators (apostrophes) and % sign
      let cleaned = value.replace(/'/g, '').replace(/%/g, '').trim();
      return cleaned === '' ? null : cleaned;
    };

    // Create complete row object with only bilateral fields (original values are not sent)
    const apiData: Cn54bisCn56BilateralRequest = {
      // Required basic fields
      sendingPpo: data.sendingPpo.trim(),
      receivingPpo: data.receivingPpo.trim(),
      year: parseInt(data.year) || 0,
      mailCategory: data.mailCategory?.trim() || 'A',
      cn54bisCn56SummaryRowType: getRowType(data.rowIndex || 0),
      
      // Bilateral data fields (user edited values) - clean thousand separators before sending
      bilateralItems: cleanValueForApi(formData.bilateralItems),
      bilateralGrossWeight: cleanValueForApi(formData.bilateralGrossWeight) || '',
      bilateralGrossIpk: cleanValueForApi(formData.bilateralGrossIpk) || '',
      // Map the current row's bilateral value to the correct field based on row index
      bilateralGrossP: cleanValueForApi(data.rowIndex === 3 ? formData.bilateralGross : formData.bilateralGrossP) || '',
      bilateralGrossG: cleanValueForApi(data.rowIndex === 4 ? formData.bilateralGross : formData.bilateralGrossG) || '',
      bilateralGrossE: cleanValueForApi(data.rowIndex === 5 ? formData.bilateralGross : formData.bilateralGrossE) || '',
      
      // Additional required fields - matching working model format
      activeCells: ['CN56_ITEMS_ORIGINAL'],
      productAsString: data.product || '',
    };

    console.log('Sending complete API data:', apiData); // Debug log
    bilateralMutation.mutate(apiData);
  };

  const handleCancel = () => {
    onClose();
  };

  if (!data) return null;

  // Check if this is a connected row (4, 5, 6) that should show all Gross % fields
  const rowIndex = data.rowIndex || 0;
  const isConnectedRow = rowIndex >= 3 && rowIndex <= 5; // Rows 4, 5, 6 (0-based: 3, 4, 5)

  // Get disabled fields for this record based on accounting type and row index
  const isTotal = data.service === 'total' || data.mailCategory === 'total';
  const disabledFields = getDisabledFieldsCn54bis(data.accountingType || 'G', data.rowIndex || 0, isTotal);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="edit-cn54bis-cn56-modal"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '70vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#E7EFF5',
          color: '#063142',
          fontWeight: 700,
          fontSize: '18px',
          borderBottom: '1px solid #ddd',
        }}
      >
                 Edit CN54bis CN56 Data
        <IconButton
          onClick={onClose}
          sx={{
            color: '#063142',
            '&:hover': {
              backgroundColor: 'rgba(6, 49, 66, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }}>
            Record Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Sending PPO"
                value={formData.sendingPpo}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Receiving PPO"
                value={formData.receivingPpo}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Year"
                value={formData.year}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Service"
                value={formData.service}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Mail Category"
                value={formData.mailCategory}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Format"
                value={formData.format}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Product"
                value={formData.product}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }}>
            Edit Bilateral Data
          </Typography>
          
          <Grid container spacing={3}>
            {/* CN56 Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#255e7c' }}>
                CN56
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      Items (Original)
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="textPrimary">
                      {data.originalItems !== null && data.originalItems !== undefined ? data.originalItems : '-'}
                    </Typography>
                  </Box>
                </Grid>
                                 <Grid item xs={6}>
                  <TextField
                    label="Items (Bilateral)"
                    value={formData.bilateralItems}
                     onChange={handleInputChange('bilateralItems')}
                     fullWidth
                     size="small"
                     disabled={disabledFields.cn56ItemsBilateral}
                     error={!!formErrors.bilateralItems}
                     helperText={
                       disabledFields.cn56ItemsBilateral 
                         ? "Disabled field"
                         : formErrors.bilateralItems || "Integer value only (e.g., 123)"
                     }
                     inputProps={{
                       inputMode: 'numeric',
                       pattern: '[0-9-]*'
                     }}
                   />
                 </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      Gross Weight (Original)
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="textPrimary">
                      {data.originalGrossWeight !== null && data.originalGrossWeight !== undefined ? data.originalGrossWeight : '-'}
                    </Typography>
                  </Box>
                </Grid>
                                 <Grid item xs={6}>
                  <TextField
                    label="Gross Weight (Bilateral)"
                    value={formData.bilateralGrossWeight}
                     onChange={handleInputChange('bilateralGrossWeight')}
                     fullWidth
                     size="small"
                     disabled={disabledFields.cn56GrossWeightBilateral}
                     error={!!formErrors.bilateralGrossWeight}
                     helperText={
                       disabledFields.cn56GrossWeightBilateral 
                         ? "Disabled field"
                         : formErrors.bilateralGrossWeight || "Exactly 1 decimal place required (e.g., 123.4)"
                     }
                     inputProps={{
                       inputMode: 'decimal',
                       step: '0.1'
                     }}
                   />
                 </Grid>
              </Grid>
            </Grid>

                         {/* CN54bis Section */}
             <Grid item xs={12}>
               <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 600, color: '#255e7c' }}>
                 CN54bis
               </Typography>
             </Grid>

            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      Gross IPK (Original)
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="textPrimary">
                      {data.originalGrossIpk !== null && data.originalGrossIpk !== undefined ? data.originalGrossIpk : '-'}
                    </Typography>
                  </Box>
                </Grid>
                                 <Grid item xs={6}>
                  <TextField
                    label="Gross IPK (Bilateral)"
                    value={formData.bilateralGrossIpk}
                     onChange={handleInputChange('bilateralGrossIpk')}
                     fullWidth
                     size="small"
                     disabled={disabledFields.cn54bisGrossIpkBilateral}
                     error={!!formErrors.bilateralGrossIpk}
                     helperText={
                       disabledFields.cn54bisGrossIpkBilateral 
                         ? "Disabled field"
                         : formErrors.bilateralGrossIpk || "Exactly 4 decimal places required (e.g., 123.4567)"
                     }
                     inputProps={{
                       inputMode: 'decimal',
                       step: '0.0001'
                     }}
                   />
                 </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      {isConnectedRow 
                        ? (rowIndex === 3 ? 'Gross P % (Original)' : rowIndex === 4 ? 'Gross G % (Original)' : 'Gross E % (Original)')
                        : 'Gross P % (Original)'}
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="textPrimary">
                      {data.originalGross !== null && data.originalGross !== undefined ? data.originalGross : '-'}
                    </Typography>
                  </Box>
                </Grid>
                                 <Grid item xs={6}>
                  <TextField
                    label={isConnectedRow 
                      ? (rowIndex === 3 ? 'Gross P % (Bilateral)' : rowIndex === 4 ? 'Gross G % (Bilateral)' : 'Gross E % (Bilateral)')
                      : 'Gross P % (Bilateral)'}
                    value={formData.bilateralGross}
                     onChange={handleInputChange('bilateralGross')}
                     fullWidth
                     size="small"
                     disabled={disabledFields.cn54bisGrossBilateral}
                     error={!!formErrors.bilateralGross}
                     helperText={
                       disabledFields.cn54bisGrossBilateral 
                         ? "Disabled field"
                         : formErrors.bilateralGross || "Exactly 2 decimal places required (e.g., 123.45 or 123.45%)"
                     }
                     inputProps={{
                       inputMode: 'decimal',
                       step: '0.01'
                     }}
                   />
                 </Grid>
              </Grid>
            </Grid>

            {/* Connected Gross % Fields - Only show for rows 4, 5, 6 */}
            {isConnectedRow && (
              <>

                {/* Show the OTHER two rows, not the current one */}
                {rowIndex !== 3 && ( // Don't show Row 4 if we're currently editing Row 4
                  <Grid item xs={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ pt: 1, pb: 1 }}>
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                            Gross P % (Original)
                          </Typography>
                          <Typography variant="body1" fontWeight={500} color="textPrimary">
                            {data.connectedRows?.find(r => r.rowIndex === 3)?.originalGross ?? '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Gross P % (Bilateral)"
                          value={formData.bilateralGrossP}
                          onChange={handleInputChange('bilateralGrossP')}
                          fullWidth
                          size="small"
                          disabled={disabledFields.cn54bisGrossBilateral}
                          error={!!formErrors.bilateralGrossP}
                          helperText={
                            disabledFields.cn54bisGrossBilateral 
                              ? "Disabled field"
                              : formErrors.bilateralGrossP || "Exactly 2 decimal places required (e.g., 22.22 or 22.22%)"
                          }
                          inputProps={{
                            inputMode: 'decimal',
                            step: '0.01'
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {rowIndex !== 4 && ( // Don't show Row 5 if we're currently editing Row 5
                  <Grid item xs={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ pt: 1, pb: 1 }}>
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                            Gross G % (Original)
                          </Typography>
                          <Typography variant="body1" fontWeight={500} color="textPrimary">
                            {data.connectedRows?.find(r => r.rowIndex === 4)?.originalGross ?? '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Gross G % (Bilateral)"
                          value={formData.bilateralGrossG}
                          onChange={handleInputChange('bilateralGrossG')}
                          fullWidth
                          size="small"
                          disabled={disabledFields.cn54bisGrossBilateral}
                          error={!!formErrors.bilateralGrossG}
                          helperText={
                            disabledFields.cn54bisGrossBilateral 
                              ? "Disabled field"
                              : formErrors.bilateralGrossG || "Exactly 2 decimal places required (e.g., 15.33 or 15.33%)"
                          }
                          inputProps={{
                            inputMode: 'decimal',
                            step: '0.01'
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {rowIndex !== 5 && ( // Don't show Row 6 if we're currently editing Row 6
                  <Grid item xs={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ pt: 1, pb: 1 }}>
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                            Gross E % (Original)
                          </Typography>
                          <Typography variant="body1" fontWeight={500} color="textPrimary">
                            {data.connectedRows?.find(r => r.rowIndex === 5)?.originalGross ?? '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Gross E % (Bilateral)"
                          value={formData.bilateralGrossE}
                          onChange={handleInputChange('bilateralGrossE')}
                          fullWidth
                          size="small"
                          disabled={disabledFields.cn54bisGrossBilateral}
                          error={!!formErrors.bilateralGrossE}
                          helperText={
                            disabledFields.cn54bisGrossBilateral 
                              ? "Disabled field"
                              : formErrors.bilateralGrossE || "Exactly 2 decimal places required (e.g., 62.45 or 62.45%)"
                          }
                          inputProps={{
                            inputMode: 'decimal',
                            step: '0.01'
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: '1px solid #ddd',
          backgroundColor: '#fafafa',
        }}
      >
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            color: '#063142',
            borderColor: '#063142',
            '&:hover': {
              borderColor: '#063142',
              backgroundColor: 'rgba(6, 49, 66, 0.05)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={bilateralMutation.isPending}
          startIcon={bilateralMutation.isPending ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            backgroundColor: '#255e7c',
            '&:hover': {
              backgroundColor: '#1e4a63',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            },
          }}
        >
          {bilateralMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}